import { idx, positionMilli } from "./board.js";
import { simulate, type BattleState } from "./simulate.js";
import { unitStats } from "./data.js";
import { MAX_BATTLE_TICKS } from "./constants.js";
import { TOWER_KINDS, type BattleInput, type BattleResult, type TowerKind } from "./types.js";

/**
 * Turning a battle into something that can be drawn (S4-1, S4-2).
 *
 * Three places need per-tick positions: the server, when it has to hand a
 * fogged attacker a stream they cannot re-run themselves; the replay viewer,
 * when it re-runs a battle to verify it; and the editor's dry run. Those three
 * used to carry a copy of this loop each, which is three chances for the
 * defender's replay and the attacker's replay to disagree about the same
 * battle — the exact failure the determinism work exists to rule out. So the
 * loop lives here, once, next to the simulation it observes.
 *
 * Nothing in this file affects the result. It reads the state the simulation
 * publishes after each tick and writes it down.
 */

/** Keep the complete battle, including late waves and timeouts. */
export const MAX_TRACE_TICKS = MAX_BATTLE_TICKS;

/**
 * Ticks of quiet kept after the last thing that happened.
 *
 * A trace ends when there is nothing left to show — plus the time it takes to
 * show it. The last unit dying is an event on the final tick, and cutting the
 * stream there would freeze it half-faded on screen forever, which reads as a
 * bug rather than as a death. A second of empty frames (the renderer fades a
 * death over 8 ticks) costs a few dozen
 * bytes and buys the end of the battle.
 */
export const TRACE_TAIL_TICKS = 10;

/**
 * What happened this tick that is worth animating, as flat quads:
 * `[code, a, b, c, code, a, b, c, ...]`.
 *
 * Flat integers rather than objects for the same reason unit positions are
 * flat: this array crosses the wire on every frame of a fogged replay, and a
 * battle can hold a few hundred of them. The codes are frozen — a renderer that
 * does not know a code must skip it, so new ones may be added but old ones
 * never change meaning.
 */
export const FX_FIRE = 1;
/** a: unit id. b: tower tile index, or -1. c: damage dealt. */
export const FX_HIT = 2;
/** a: unit id. b: tower tile index that finished it, or -1. */
export const FX_DEATH = 3;
/** a: unit id. */
export const FX_SLOW = 4;
/** a: unit id. Reached the core — the attacker's win, drawn differently. */
export const FX_REACH = 5;
/** a: BOARD tile index of the barricade that fell (row-major, like FX_FIRE's tower tile). */
export const FX_BARRICADE = 6;
/** a: board tile index of a tower that just went offline (below the HP threshold). */
export const FX_TOWER_DOWN = 7;

/** How many numbers each fx entry occupies. */
export const FX_STRIDE = 4;

export interface Frame {
  readonly tick: number;
  /** Flat triples: unit id, x in milli-tiles, y in milli-tiles. */
  readonly units: readonly number[];
  /** Flat quads: code, a, b, c. See the FX_* codes above. */
  readonly fx: readonly number[];
  /**
   * Health of each unit in `units`, in the same order, as thousandths of its
   * full HP (0..1000). Drawn as the bar over its head.
   *
   * Optional because a stream recorded before it existed does not carry it; a
   * renderer without it simply draws no bars. It is the attacker's OWN army's
   * health, so a fogged stream may carry it without leaking anything.
   */
  readonly hp?: readonly number[];
}

export interface Trace {
  readonly result: BattleResult;
  readonly frames: readonly Frame[];
  /**
   * Tile index of each tower, by its simulation id.
   *
   * Handed back so nobody has to re-derive the ordering. The server needs it to
   * turn `result.reveal.towerIds` into "which squares may this attacker see a
   * shot come from", and a second implementation of "how towers are numbered"
   * is a second thing that can disagree with the first.
   */
  readonly towerTiles: readonly number[];
}

/**
 * Run a battle and write down what a viewer would have seen.
 *
 * `fire` events name a tower by its TILE INDEX rather than by the simulation's
 * internal tower id. Two reasons, and both matter: a renderer knows where a
 * tile is and has no idea what a tower id means, and the server has to be able
 * to drop the ones a fogged attacker never earned — which is a decision about
 * board positions, not about array offsets.
 */
export function traceBattle(input: BattleInput, maxTicks: number = MAX_TRACE_TICKS): Trace {
  const frames: Frame[] = [];
  /** tower id -> tile index, built once; towers do not move. */
  let towerTile: number[] = [];
  let seen = 0;

  const result = simulate(input, (state: BattleState) => {
    if (state.tick >= maxTicks) return;

    if (towerTile.length === 0 && state.towers.length > 0) {
      towerTile = [];
      for (const tower of state.towers) {
        towerTile[tower.id] = idx(tower.at.x, tower.at.y, input.layout.size);
      }
    }

    const units: number[] = [];
    const hp: number[] = [];
    for (const unit of state.units) {
      // Only units actually on the road have a position worth drawing.
      if (unit.status !== "walking" && unit.status !== "blocked") continue;
      const at = positionMilli(state.path, unit.progress);
      units.push(unit.id, at.x, at.y);
      const full = unitStats(unit.kind).hp;
      hp.push(full > 0 ? Math.max(0, Math.min(1000, Math.floor((unit.hp * 1000) / full))) : 1000);
    }

    // `state.events` accumulates for the whole battle, so this tick's share is
    // whatever arrived since the last time we looked.
    const fx: number[] = [];
    for (let i = seen; i < state.events.length; i++) {
      const event = state.events[i]!;
      switch (event.type) {
        case "fire":
          fx.push(FX_FIRE, towerTile[event.a ?? -1] ?? -1, event.b ?? -1, kindCode(state, event.a));
          break;
        case "hit":
          fx.push(FX_HIT, event.a ?? -1, towerTile[event.b ?? -1] ?? -1, event.c ?? 0);
          break;
        case "death":
          fx.push(FX_DEATH, event.a ?? -1, towerTile[event.b ?? -1] ?? -1, 0);
          break;
        case "slow":
          fx.push(FX_SLOW, event.a ?? -1, towerTile[event.b ?? -1] ?? -1, 0);
          break;
        case "reach":
          fx.push(FX_REACH, event.a ?? -1, -1, 0);
          break;
        case "barricade_down": {
          // The simulation names a barricade by its place along the road; a
          // renderer only knows board squares, so translate once, here.
          const at = state.path[event.a ?? -1];
          fx.push(FX_BARRICADE, at ? idx(at.x, at.y, input.layout.size) : -1, -1, 0);
          break;
        }
        case "tower_offline":
          fx.push(FX_TOWER_DOWN, towerTile[event.a ?? -1] ?? -1, -1, 0);
          break;
        default:
          break; // spawn and blocked are visible in the positions themselves
      }
    }
    seen = state.events.length;

    frames.push({ tick: state.tick, units, fx, hp });
  });

  // Trailing frames with nothing in them are just the battle being over — but
  // keep a moment of them, so whatever happened last has time to finish.
  let end = frames.length;
  while (end > 0 && frames[end - 1]!.units.length === 0 && frames[end - 1]!.fx.length === 0) {
    end -= 1;
  }
  const want = Math.min(maxTicks, end + TRACE_TAIL_TICKS);
  const kept = frames.slice(0, Math.min(frames.length, want));
  // A battle that ENDS on its last death (everyone dead, or everyone in) has no
  // frames after it to keep — the simulation simply stopped. Pad with empty
  // ones, or the last unit freezes mid-fall on screen, which reads as a bug.
  // Display only: these frames are not part of the result or its hash.
  let tick = kept.length > 0 ? kept[kept.length - 1]!.tick : -1;
  while (kept.length > 0 && kept.length < want) {
    tick += 1;
    kept.push({ tick, units: [], fx: [], hp: [] });
  }

  return { result, frames: kept, towerTiles: towerTile };
}

function kindCode(state: BattleState, towerId: number | undefined): number {
  const tower = state.towers.find((t) => t.id === towerId);
  return tower ? TOWER_KINDS.indexOf(tower.kind as TowerKind) : -1;
}

/** Read one fx entry out of a frame's flat array. */
export function fxAt(
  fx: readonly number[],
  i: number
): { code: number; a: number; b: number; c: number } {
  const at = i * FX_STRIDE;
  return { code: fx[at] ?? 0, a: fx[at + 1] ?? -1, b: fx[at + 2] ?? -1, c: fx[at + 3] ?? 0 };
}

/** How many fx entries a frame holds. */
export const fxCount = (fx: readonly number[]): number => Math.floor(fx.length / FX_STRIDE);
